import type { LeagueItem } from 'entities/leagues'
import type { Organization } from 'entities/organizations'
import type { ApplicationCategoryType } from 'entities/phases'
import type { Season } from 'entities/seasons'
import type { Sponsor } from 'entities/sponsors'
import type { Sport } from 'entities/sports'

export type Competition = {
    applicationCategories: ApplicationCategoryType[]
    id: string
    name: string
    sex: Sex[]
    status: CompetitionStatus
    generalApplicationStartDate: string
    generalApplicationEndDate: string
    individualApplicationStartDate: string
    individualApplicationEndDate: string
    competitionStartDate: string
    competitionEndDate: string
    isGeneralApplicationEndDateLocked: boolean
    isIndividualApplicationEndDateLocked: boolean
    isCompetitionEndDateLocked: boolean
    location: string
    locationRemark: string
    sequenceName: string
    sequenceNumber: number
    tableStandings: TableStandings
    participantTypes: ParticipantTypes[]
    numMaxDisciplineCompetitors: number
    statistics: Statistics[]
    sportId: string
    seasonId: string
    league: LeagueItem
    leagueId: string
    organization: Organization
    organizationId: string
    sport: Sport
    season: Season
    createdAt: string
    updatedAt: string
    sponsors: Sponsor[]
    sponsorIds: string[]
    disciplineIds: string[]
    comment?: string
    shouldSendUpdateEmail?: boolean
}

export enum Sex {
    MALE = 'male',
    FEMALE = 'female',
}

export enum CompetitionStatus {
    NOT_STARTED = 'not-started',
    GENERAL_APPLICATIONS = 'general-applications',
    INDIVIDUAL_APPLICATIONS = 'individual-applications',
    CLOSED_APPLICATIONS = 'closed-applications',
    ONGOING = 'ongoing',
    FINISHED = 'finished',
    UNDEFINED = 'undefined',
}
export enum ApplicationCallStatus {
    INVITED = 'invited',
    PENDING = 'pending',
    DECLINED = 'declined',
    APPROVED = 'approved',
    REJECTED = 'rejected',
}

export enum TableStandings {
    GOAL_DIFFERENCE = 'goal-difference',
    MUTUAL_TRIALS = 'mutual-trials',
}

export enum ParticipantTypes {
    TEAM = 'team',
    INDIVIDUAL = 'individual',
    TEAM_INDIVIDUAL = 'team-individual',
}

export type ParticipantEnumType = keyof typeof ParticipantTypes

export enum Statistics {
    GOAL = 'goal',
    ASSIST = 'assist',
    FOUL = 'foul',
    BLOCKADE = 'blockade',
    BASKET = 'basket',
    TWO_POINTER = 'two-pointer',
    THREE_POINTER = 'three-pointer',
    BLOCKED_SHOT = 'blocked-shot',
    SHOT = 'shot',
    SHOT_INSIDE_GOAL_FRAME = 'shot-inside-goal-frame',
    SHOT_OUTSIDE_GOAL_FRAME = 'shot-outside-goal-frame',
    CROSSBAR_OR_POST = 'crossbar-or-post',
    RED_CARD = 'red-card',
    YELLOW_CARD = 'yellow-card',
    BLUE_CARD = 'blue-card',
    POINT = 'point',
    EXCLUSION = 'exclusion',
    EXCLUSION_2_MINUTES = 'exclusion-2-minutes',
    EXCLUSION_20_SECONDS = 'exclusion-20-seconds',
    LOST_BALL = 'lost-ball',
    STOLEN_BALL = 'stolen-ball',
    ATTACK_FOUL = 'attack-foul',
    ATTACK_MISTAKE = 'attack-mistake',
    UNSPORTMANLIKE_FOUL = 'unsportmanlike-foul',
    OWN_GOAL = 'own-goal',
    JUMP = 'jump',
    TECHNICAL_ERROR = 'technical-error',
    MVP = 'mvp',
    TIME_OUT = 'time-out',
    CORNER = 'corner',
    VICTORY = 'victory',
    DRAW = 'draw',
    DEFEAT = 'defeat',
    APPEARANCE = 'appearance',
}

export interface MatchesResponseParams {
    params?: string
    phaseIds?: string
    page?: number
    limit?: number
}

export interface CompetitionResponseParams {
    params?: string
    update?: boolean
    page?: number
    limit?: number
    sportIds?: string
    organizationIds?: string
    unitIds?: string
    leagueIds?: string
    seasonIds?: string
    sexes?: string
    statuses?: string
    sortBy?: SortByType
    search?: string
    orderBy?: OrderByType
    applicationStatuses?: string
    competitionStatuses?: string
    competitionId?: string
    unitId?: string
}

export enum OrderByType {
    ASC = 'ASC',
    DESC = 'DESC',
}

export enum SortByType {
    SEASON_DATE_START = 'season.dateStart',
    GENERAL_APPLICATION_END_DATE = 'generalApplicationEndDate',
    INDIVIDUAL_APPLICATION_END_DATE = 'individualApplicationEndDate',
    COMPETITION_START_DATE = 'competitionStartDate',
}

export interface CompetitionRequestBody {
    name: string
    sex: Sex
    generalApplicationStartDate: string
    generalApplicationEndDate: string
    individualApplicationStartDate: string
    individualApplicationEndDate: string
    competitionStartDate: string
    competitionEndDate: string
    isGeneralApplicationEndDateLocked: boolean
    isIndividualApplicationEndDateLocked: boolean
    isCompetitionEndDateLocked: boolean
    location: string
    locationRemark?: string
    sequenceName: string
    sequenceNumber: number
    tableStandings: TableStandings
    participantTypes: ParticipantEnumType[]
    numMaxDisciplineCompetitors: number
    statistics: Statistics[]
    sportId: string
    seasonId: string
    leagueId: string
    organizationId: string
    disciplineIds: string[]
    sponsorIds: string[]
}

export interface CompetitionDocumentRequestBody {
    name: string
    description: string
    key: string
    type: DocumentType
    displayName: string
    id?: number
}

export interface CompetitionDocument {
    id: number
    name: string
    description: string
    key: string
    type: DocumentType
    createdAt: string
    updatedAt: string
    competitionId: string
}

export enum DocumentType {
    REGULATIONS = 'regulations',
    PROPOSALS = 'proposals',
    INSTRUCTIONS = 'instructions',
    OTHER = 'other',
}

export interface CompetitionResponseData {
    id: number
    name: string
    description: string
    competitionId: string
    key: string
    type: DocumentType
    createdAt: string
    updatedAt: string
}

export interface CompetitionIUrlResponse {
    url: string
    key: string
}
